import { FoquzComponent } from 'Models/foquz-component';
import { debounce } from 'lodash';

const MOBILE_BREAKPOINT = 768;

export class ViewModel extends FoquzComponent {
  constructor(params, element) {
    super(params, element);

    this.imageSrc = ko.isObservable(params.imageSrc) ? params.imageSrc : ko.observable(params.imageSrc);
    this.imageWidth = ko.isObservable(params.imageWidth) ? params.imageWidth : ko.observable(params.imageWidth);
    this.imageHeight = ko.isObservable(params.imageHeight) ? params.imageHeight : ko.observable(params.imageHeight);
    this.title = params.title;

    this.size = params.size;
    this.clicks = ko.isObservable(params.clicks) ? params.clicks : ko.observableArray(params.clicks || []);

    this.displayedImageDimensions = ko.observable({ width: 0, height: 0 });
    this.imageOffset = ko.observable({ x: 0, y: 0 });
    this.containerElement = element.querySelector('.user-click-areas-showcase__container');
    this.imageElement = element.querySelector('.user-click-areas-showcase__image');
    
    this.activeClickZIndex = ko.observable(10);

    this.processedClicks = ko.computed(() => {
      const clicksArray = ko.unwrap(this.clicks);
      const originalImageWidth = ko.unwrap(this.imageWidth);
      const originalImageHeight = ko.unwrap(this.imageHeight);
      
      return clicksArray.map((click, index) => {
        const percentX = ko.computed(() => originalImageWidth ? (click.x / originalImageWidth) * 100 : 0);
        const percentY = ko.computed(() => originalImageHeight ? (click.y / originalImageHeight) * 100 : 0);

        const clickItem = {
          ...click,
          id: `click-indicator-${index}`,
          percentX: percentX,
          percentY: percentY,
          pixelX: ko.computed(() => (percentX() / 100) * this.displayedImageDimensions().width + this.imageOffset().x),
          pixelY: ko.computed(() => (percentY() / 100) * this.displayedImageDimensions().height + this.imageOffset().y),
          areaName: click.areaName,
          timeInSeconds: click.timeInSeconds,
          zIndex: ko.observable(1),
          labelStyle: ko.observable({})
        };
        
        return clickItem;
      });
    });

    this.subscriptions.push(this.processedClicks.subscribe(() => {
      setTimeout(() => {
        ko.unwrap(this.processedClicks).forEach(clickItem => this.updateLabelPosition(clickItem));
      }, 0);
    }));
    
    this.debouncedUpdateDimensions = debounce(this.updateDimensions.bind(this), 100);
    this.resizeObserver = null;

    this.init(params);
  }

  init(params) {
    this.subscriptions.push(this.imageSrc.subscribe(() => this.loadImage()));
    this.subscriptions.push(this.imageWidth.subscribe(() => this.debouncedUpdateDimensions()));
    this.subscriptions.push(this.imageHeight.subscribe(() => this.debouncedUpdateDimensions()));
    this.subscriptions.push(this.clicks.subscribe(() => this.debouncedUpdateDimensions()));

    this.loadImage();
    this.setupResizeObserver();
  }

  loadImage() {
    const src = ko.unwrap(this.imageSrc);
    if (!src) {
      this.displayedImageDimensions({ width: 0, height: 0 });
      this.imageOffset({ x: 0, y: 0 });
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (!ko.unwrap(this.imageWidth) || !ko.unwrap(this.imageHeight)) {
        this.imageWidth(img.naturalWidth);
        this.imageHeight(img.naturalHeight);
      }
      this.debouncedUpdateDimensions();
    };
    img.onerror = () => {
      console.error('Failed to load image:', src);
      this.displayedImageDimensions({ width: 0, height: 0 });
      this.imageOffset({ x: 0, y: 0 });
    };
    img.src = src;
  }

  updateDimensions() {
    if (!this.imageElement || !ko.unwrap(this.imageSrc)) {
      this.displayedImageDimensions({ width: 0, height: 0 });
      this.imageOffset({ x: 0, y: 0 });
      return;
    }

    const imageRect = this.imageElement.getBoundingClientRect();
    const containerRect = this.containerElement.getBoundingClientRect();

    this.displayedImageDimensions({ width: imageRect.width, height: imageRect.height });

    const offsetX = imageRect.left - containerRect.left;
    const offsetY = imageRect.top - containerRect.top;
    this.imageOffset({ x: offsetX, y: offsetY });

    ko.unwrap(this.processedClicks).forEach(clickItem => this.updateLabelPosition(clickItem));
  }

  setupResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.containerElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.debouncedUpdateDimensions();
      });
      this.resizeObserver.observe(this.containerElement);
    }
  }
  
	updateLabelPosition(clickItem) {
		if (!this.containerElement) return;

		const labelElement = this.containerElement.querySelector(`#${clickItem.id}-label`);
		if (!labelElement) return;

		const labelRect = labelElement.getBoundingClientRect();
		const labelWidth = labelRect.width;
		const labelHeight = labelRect.height;

		const indicatorHeight = 16;
		const OFFSET = 5;

		const indicatorCenterX = (clickItem.percentX() / 100) * this.displayedImageDimensions().width;
		const indicatorCenterY = (clickItem.percentY() / 100) * this.displayedImageDimensions().height;

		let labelLeft = indicatorCenterX - (labelWidth / 2);
		let labelTop = indicatorCenterY + (indicatorHeight / 2) + OFFSET;
		let position = 'bottom';

		const displayedWidth = this.displayedImageDimensions().width;
		const displayedHeight = this.displayedImageDimensions().height;

		const wouldOverflowBottom = labelTop + labelHeight > displayedHeight;
		const wouldOverflowTop = indicatorCenterY - (indicatorHeight / 2) - labelHeight - OFFSET < 0;

		if (wouldOverflowBottom && !wouldOverflowTop) {
			labelTop = indicatorCenterY - (indicatorHeight / 2) - labelHeight - OFFSET;
			position = 'top';
		}
		else if (wouldOverflowBottom && wouldOverflowTop) {
			labelTop = displayedHeight - labelHeight;
		}

		if (labelLeft < 0) {
			labelLeft = 0;
		}
		if (labelLeft + labelWidth > displayedWidth) {
			labelLeft = displayedWidth - labelWidth;
		}

		const overlaps = this.checkLabelOverlaps(clickItem.id, labelLeft, labelTop, labelWidth, labelHeight);

		if (overlaps && position === 'bottom' && !wouldOverflowTop) {
			const newTop = indicatorCenterY - (indicatorHeight / 2) - labelHeight - OFFSET;
			const newOverlaps = this.checkLabelOverlaps(clickItem.id, labelLeft, newTop, labelWidth, labelHeight);

			if (!newOverlaps || newOverlaps.length < overlaps.length) {
				labelTop = newTop;
				position = 'top';
			}
		}

		clickItem.labelStyle({
			top: `${labelTop}px`,
			left: `${labelLeft}px`,
			visibility: 'visible',
			opacity: 1
		});

		clickItem.currentLabelPosition = { left: labelLeft, top: labelTop, width: labelWidth, height: labelHeight };
	}

	checkLabelOverlaps(currentId, left, top, width, height) {
		const otherLabels = ko.unwrap(this.processedClicks).filter(click =>
			click.id !== currentId && click.currentLabelPosition
		);

		return otherLabels.filter(click => {
			const other = click.currentLabelPosition;
			return !(
				left + width < other.left ||
				left > other.left + other.width ||
				top + height < other.top ||
				top > other.top + other.height
			);
		});
	}

  onIndicatorClick(clickItem) {
    this.activeClickZIndex(this.activeClickZIndex() + 1);
    clickItem.zIndex(this.activeClickZIndex());
  }

  dispose() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    super.dispose();
  }
}
